<template>
    <div class="flex items-center justify-between mb-8">
        <Head :title="$t('agencies.brandingSettings')" />
        <page-title :title="$t('agencies.brandingSettings')"></page-title>
    </div>

    <!-- Branding Settings -->
    <div class="mt-10 sm:mt-0">
        <div class="flex flex-col lg:flex-row gap-8">
            <!-- Left Side: Preview -->
            <div class="lg:w-1/3">
                <div class="sticky top-6">
                    <h3 class="text-lg font-medium text-gray-900 mb-4">
                        {{ $t('preview') || 'Vorschau' }}
                    </h3>
                    <branding-preview 
                        :navbar-color="form.branding_navbar_color"
                        :sidebar-color="form.branding_sidebar_color"
                        :logo-url="logoPreviewUrl || (agency.agency.branding_logo_path ? getLogoUrl(agency.agency.branding_logo_path) : null)"
                    />
                </div>
            </div>

            <!-- Right Side: Form -->
            <div class="lg:w-2/3">
                <div class="px-4 sm:px-0 mb-6 lg:hidden">
                    <h3 class="text-lg font-medium text-gray-900">
                        {{ $t("agencies.branding") }}
                    </h3>
                    <p class="mt-1 text-sm text-gray-600">
                        {{ $t("agencies.brandingSettings") }}
                    </p>
                </div>
                
                <form @submit.prevent="submit">
                    <div class="shadow-lg sm:rounded-xl overflow-hidden border border-gray-100 bg-white">
                        <div class="px-6 py-8">
                            <div class="space-y-8">
                                
                                <!-- Logo Section -->
                                <div class="border-b border-gray-100 pb-8 last:border-0 last:pb-0">
                                    <label class="block text-sm font-semibold text-gray-800 uppercase tracking-wider mb-4">{{ $t("agencies.logo") }}</label>
                                    <div class="flex flex-col sm:flex-row items-start space-y-4 sm:space-y-0 sm:space-x-8">
                                        <div class="flex-shrink-0">
                                            <div v-if="logoPreviewUrl || agency.agency.branding_logo_path" class="p-2 bg-gray-50 rounded-xl border-2 border-dashed border-gray-200 group hover:border-indigo-300 transition-colors w-[240px] h-[120px] flex items-center justify-center overflow-hidden">
                                                <img :src="logoPreviewUrl || getLogoUrl(agency.agency.branding_logo_path)" class="max-w-full max-h-full object-contain" alt="Agency Logo" />
                                            </div>
                                            <div v-else class="w-[240px] h-[120px] bg-gray-50 rounded-xl border-2 border-dashed border-gray-200 flex items-center justify-center text-gray-400">
                                                <svg class="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path></svg>
                                            </div>
                                        </div>
                                        <div class="flex-1 space-y-3 w-full">
                                            <div class="flex items-center justify-center w-full">
                                                <label class="flex flex-col items-center justify-center w-full h-[80px] border-2 border-gray-300 border-dashed rounded-xl cursor-pointer bg-gray-50 hover:bg-gray-100 transition-colors">
                                                    <div class="flex flex-col items-center justify-center pt-2 pb-2 text-center">
                                                        <svg class="w-6 h-6 mb-1 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"></path></svg>
                                                        <p class="text-[11px] text-gray-500 font-medium px-2"><span class="font-bold text-indigo-600">{{ $t('click_to_upload') || 'Klicken zum Hochladen' }}</span> {{ $t('or_drag_and_drop') || 'oder Drag & Drop' }}</p>
                                                    </div>
                                                    <input type="file" ref="logoInput" class="hidden" @change="handleFileUpload" :accept="allowedMimesAccept" />
                                                </label>
                                            </div>
                                            <p class="text-[11px] text-red-400 italic">Elaubte Formate: {{ branding.logo_upload_mimes.toUpperCase() }}, Max {{ maxSizeInMb }} MB. Empfohlene Maße: 240x120 Pixel.</p>
                                        </div>
                                    </div>
                                </div>

                                <!-- Colors Section -->
                                <div class="grid grid-cols-1 md:grid-cols-2 gap-8 pt-4">
                                    <!-- Navbar Color -->
                                    <div class="space-y-3">
                                        <label for="navbar_color" class="block text-sm font-semibold text-gray-800 uppercase tracking-wider">{{ $t("agencies.navbarColor") }}</label>
                                        <div class="flex items-center space-x-3">
                                            <div class="relative flex-1">
                                                <input
                                                    type="text"
                                                    name="navbar_color"
                                                    id="navbar_color"
                                                    v-model="form.branding_navbar_color"
                                                    class="focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-xl pl-10 h-11"
                                                    placeholder="#RRGGBB"
                                                />
                                                <div class="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                                                    <span class="text-gray-400 font-mono">#</span>
                                                </div>
                                            </div>
                                            <div class="relative">
                                                <input
                                                    type="color"
                                                    v-model="form.branding_navbar_color"
                                                    class="h-11 w-14 p-1 border border-gray-300 rounded-xl cursor-pointer bg-white overflow-hidden"
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <!-- Sidebar Color -->
                                    <div class="space-y-3">
                                        <label for="sidebar_color" class="block text-sm font-semibold text-gray-800 uppercase tracking-wider">{{ $t("agencies.sidebarColor") }}</label>
                                        <div class="flex items-center space-x-3">
                                            <div class="relative flex-1">
                                                <input
                                                    type="text"
                                                    name="sidebar_color"
                                                    id="sidebar_color"
                                                    v-model="form.branding_sidebar_color"
                                                    class="focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-xl pl-10 h-11"
                                                    placeholder="#RRGGBB"
                                                />
                                                <div class="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                                                    <span class="text-gray-400 font-mono">#</span>
                                                </div>
                                            </div>
                                            <div class="relative">
                                                <input
                                                    type="color"
                                                    v-model="form.branding_sidebar_color"
                                                    class="h-11 w-14 p-1 border border-gray-300 rounded-xl cursor-pointer bg-white overflow-hidden"
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <!-- Navbar Text Color -->
                                    <div class="space-y-3">
                                        <label for="branding_navbar_text_color" class="block text-sm font-semibold text-gray-800 uppercase tracking-wider">{{ $t("agencies.navbarTextColor") || 'Navbar Text Farbe' }}</label>
                                        <div class="flex items-center space-x-3">
                                            <div class="relative flex-1">
                                                <input
                                                    type="text"
                                                    name="branding_navbar_text_color"
                                                    id="branding_navbar_text_color"
                                                    v-model="form.branding_navbar_text_color"
                                                    class="focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-xl pl-10 h-11"
                                                    placeholder="#RRGGBB"
                                                />
                                                <div class="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                                                    <span class="text-gray-400 font-mono">#</span>
                                                </div>
                                            </div>
                                            <div class="relative">
                                                <input
                                                    type="color"
                                                    v-model="form.branding_navbar_text_color"
                                                    class="h-11 w-14 p-1 border border-gray-300 rounded-xl cursor-pointer bg-white overflow-hidden"
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <!-- Sidebar Text Color -->
                                    <div class="space-y-3">
                                        <label for="branding_sidebar_text_color" class="block text-sm font-semibold text-gray-800 uppercase tracking-wider">{{ $t("agencies.sidebarTextColor") || 'Sidebar Text Farbe' }}</label>
                                        <div class="flex items-center space-x-3">
                                            <div class="relative flex-1">
                                                <input
                                                    type="text"
                                                    name="branding_sidebar_text_color"
                                                    id="branding_sidebar_text_color"
                                                    v-model="form.branding_sidebar_text_color"
                                                    class="focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-xl pl-10 h-11"
                                                    placeholder="#RRGGBB"
                                                />
                                                <div class="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                                                    <span class="text-gray-400 font-mono">#</span>
                                                </div>
                                            </div>
                                            <div class="relative">
                                                <input
                                                    type="color"
                                                    v-model="form.branding_sidebar_text_color"
                                                    class="h-11 w-14 p-1 border border-gray-300 rounded-xl cursor-pointer bg-white overflow-hidden"
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <!-- Email Background Color -->
                                    <div class="space-y-3">
                                        <label for="branding_email_background_color" class="block text-sm font-semibold text-gray-800 uppercase tracking-wider">Email Hintergrundfarbe</label>
                                        <div class="flex items-center space-x-3">
                                            <div class="relative flex-1">
                                                <input
                                                    type="text"
                                                    name="branding_email_background_color"
                                                    id="branding_email_background_color"
                                                    v-model="form.branding_email_background_color"
                                                    class="focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-xl pl-10 h-11"
                                                    placeholder="#RRGGBB"
                                                />
                                                <div class="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                                                    <span class="text-gray-400 font-mono">#</span>
                                                </div>
                                            </div>
                                            <div class="relative">
                                                <input
                                                    type="color"
                                                    v-model="form.branding_email_background_color"
                                                    class="h-11 w-14 p-1 border border-gray-300 rounded-xl cursor-pointer bg-white overflow-hidden"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                
                                <!-- Changed By -->
                                <div class="pt-4" v-if="agency.agency.branding_change_by">
                                    <div class="inline-flex items-center px-3 py-1 rounded-full bg-gray-100 text-[11px] font-medium text-gray-600">
                                        <svg class="w-3 h-3 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path></svg>
                                        Last changed by: {{ agency.agency.branding_change_by }}
                                    </div>
                                </div>

                            </div>
                        </div>
                        <div class="px-6 py-5 bg-gray-50/50 border-t border-gray-100 flex justify-between">
                            <secondary-button @click="resetToDefault" type="button" class="h-11 px-6 rounded-xl border-gray-300 text-gray-700 hover:bg-gray-100 transition-all font-medium">
                                {{ $t("agencies.resetToDefault") }}
                            </secondary-button>
                            
                            <div class="flex flex-col items-end">
                                <primary-button :class="{ 'opacity-25': form.processing }" :disabled="form.processing" class="h-11 px-8 rounded-xl shadow-md transform transition-all active:scale-95">
                                    <span v-if="form.processing" class="flex items-center">
                                        <svg class="animate-spin -ml-1 mr-3 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                                        {{ $t('saving') || 'Speichert...' }}
                                    </span>
                                    <span v-else>{{ $t("agencies.publish") }}</span>
                                </primary-button>
                                <span v-if="form.isDirty" class="mt-2 text-xs text-amber-600 flex items-center animate-pulse">
                                    <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path></svg>
                                    {{ $t('unsaved_changes') || 'Nicht gespeichert' }}
                                </span>
                            </div>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    </div>
</template>

<script>
import PageTitle from "@/Shared/Components/PageTitle";
import MainLayout from "@/Layouts/MainLayout";
import SettingsLayout from "@/Layouts/SettingsLayout";
import PrimaryButton from "@/Jetstream/PrimaryButton";
import SecondaryButton from "@/Jetstream/SecondaryButton";
import BrandingPreview from "@/Shared/Components/BrandingPreview";
import { Head, useForm, usePage } from "@inertiajs/inertia-vue3";
import { useI18n } from "vue-i18n";
import { ref, onUnmounted } from "vue";
import { notify } from "notiwind";

export default {
    components: {
        Head,
        PageTitle,
        PrimaryButton,
        SecondaryButton,
        BrandingPreview,
    },
    props: ["agency"],
    layout: [MainLayout, SettingsLayout],
    setup(props) {
        const { t } = useI18n();
        const { props: pageProps } = usePage();
        const logoPreviewUrl = ref(null);

        const branding = pageProps.value.branding;
        const maxBytes = branding.max_logo_upload_size || (3 * 1024 * 1024);
        const maxSizeInMb = Math.round(maxBytes / (1024 * 1024));

        const allowedMimesList = (branding.logo_upload_mimes || 'png,svg').split(',').map(m => m.trim().toLowerCase());
        const allowedMimesAccept = allowedMimesList.map(m => m === 'svg' ? '.svg,image/svg+xml' : `image/${m}`).join(',');

        onUnmounted(() => {
            if (logoPreviewUrl.value) {
                URL.revokeObjectURL(logoPreviewUrl.value);
            }
        });
        
        const form = useForm({
            branding_logo_path: null,
            branding_navbar_color: props.agency.agency.branding_navbar_color || "",
            branding_sidebar_color: props.agency.agency.branding_sidebar_color || "",
            branding_navbar_text_color: props.agency.agency.branding_navbar_text_color || "",
            branding_sidebar_text_color: props.agency.agency.branding_sidebar_text_color || "",
            branding_email_background_color: props.agency.agency.branding_email_background_color || "",
            remove_logo: false,
        });

        const submit = () => {
            form.transform((data) => ({
                ...data,
                _method: "PUT",
            })).post(window.route("advisors.agencies.branding.update", props.agency.agency.uuid, undefined, pageProps.value.ziggy), {
                forceFormData: true,
                preserveScroll: true,
                onSuccess: () => {
                    notify({
                        group: "success",
                        title: "Erfolg",
                        text: "Branding-Einstellungen wurden erfolgreich aktualisiert."
                    }, 3000);
                },
                onError: () => {
                    notify({
                        group: "error",
                        title: "Fehler",
                        text: "Beim Speichern der Einstellungen ist ein Fehler aufgetreten."
                    }, 3000);
                }
            });
        };

        const handleFileUpload = (event) => {
            const file = event.target.files[0];
            if (file) {
                const fileExt = file.name.split('.').pop().toLowerCase();
                const fileType = file.type;

                const isAllowed = allowedMimesList.some(mime => {
                    if (mime === 'svg') return fileType === 'image/svg+xml' || fileExt === 'svg';
                    return fileType === `image/${mime}` || fileExt === mime;
                });

                if (!isAllowed) {
                    notify({
                        group: "error",
                        title: "Fehler",
                        text: `Bitte laden Sie nur ${branding.logo_upload_mimes.toUpperCase()} Dateien hoch.`
                    }, 3000);
                    event.target.value = "";
                    return;
                }
                if (file.size > maxBytes) {
                    notify({
                        group: "error",
                        title: "Fehler",
                        text: `Die Datei ist zu groß. Max ${maxSizeInMb} MB erlaubt.`
                    }, 3000);
                    event.target.value = "";
                    return;
                }
                form.branding_logo_path = file;
                form.remove_logo = false;
                logoPreviewUrl.value = URL.createObjectURL(file);
            }
        };

        const resetToDefault = () => {
             if (confirm(t('agencies.resetToDefault'))) {
                 form.branding_navbar_color = null;
                 form.branding_sidebar_color = null;
                 form.branding_navbar_text_color = null;
                 form.branding_sidebar_text_color = null;
                 form.branding_email_background_color = null;
                 form.remove_logo = true;
                 logoPreviewUrl.value = null;
                 submit();
             }
        };

        const getLogoUrl = (path) => {
            if (!path) return "";
            return window.route("media.restore", { filename: path });
        };

        return { t, form, submit, handleFileUpload, getLogoUrl, resetToDefault, logoPreviewUrl, maxSizeInMb, branding, allowedMimesAccept };
    },
};
</script>

